/**
 * EacSettingsContext
 *
 * Company-wide EAC (Estimate at Completion) settings, stored in local React
 * state backed by localStorage.  The engineering team will wire these fields to
 * GET/PUT /api/settings/finance once the backend columns are added.
 *
 * Consumers:
 *   - Settings → General → Reporting & Calculations   (read + write)
 *   - Project → Forecast tab                          (read)
 *   - Project → Financials tab                        (read)
 *   - Any component that conditionally renders the HTC column (read)
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type { EacMethod } from '@/lib/utils';

// ── localStorage keys ─────────────────────────────────────────────────────────
const LS_EAC_METHOD          = 'evo_eac_method';
const LS_SHOW_HTC            = 'evo_show_hours_to_complete';

// ── Helpers ───────────────────────────────────────────────────────────────────
function readEacMethod(): EacMethod {
  try {
    const stored = localStorage.getItem(LS_EAC_METHOD);
    if (stored === 'simple' || stored === 'performance-based' || stored === 're-estimate') {
      return stored;
    }
  } catch {
    // Storage unavailable (private browsing, blocked cookies) — use the default
  }
  return 'simple';
}

function readShowHtc(): boolean {
  try {
    return localStorage.getItem(LS_SHOW_HTC) === 'true';
  } catch {
    return false;
  }
}

function writeSetting(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage quota exceeded or unavailable — fail silently
  }
}

// ── Context shape ─────────────────────────────────────────────────────────────
export interface EacSettingsContextValue {
  /** Which EAC calculation method is active company-wide. */
  eacMethod: EacMethod;
  setEacMethod: (method: EacMethod) => void;

  /**
   * When true, a "Hours remaining" column appears on timesheet entry,
   * and feeds into EAC calculations on the Project Completion Forecast tab.
   */
  showHoursToComplete: boolean;
  setShowHoursToComplete: (show: boolean) => void;
}

const EacSettingsContext = createContext<EacSettingsContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────
export function EacSettingsProvider({ children }: { children: ReactNode }) {
  const [eacMethod, _setEacMethod] = useState<EacMethod>(readEacMethod);
  const [showHoursToComplete, _setShowHtc] = useState<boolean>(readShowHtc);

  const setEacMethod = useCallback((method: EacMethod) => {
    writeSetting(LS_EAC_METHOD, method);
    _setEacMethod(method);
  }, []);

  const setShowHoursToComplete = useCallback((show: boolean) => {
    writeSetting(LS_SHOW_HTC, String(show));
    _setShowHtc(show);
  }, []);

  const value = useMemo(
    () => ({ eacMethod, setEacMethod, showHoursToComplete, setShowHoursToComplete }),
    [eacMethod, setEacMethod, showHoursToComplete, setShowHoursToComplete],
  );

  return (
    <EacSettingsContext.Provider value={value}>
      {children}
    </EacSettingsContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useEacSettings(): EacSettingsContextValue {
  const ctx = useContext(EacSettingsContext);
  if (!ctx) {
    throw new Error('useEacSettings must be used within an EacSettingsProvider');
  }
  return ctx;
}
